/**
 * Surfaces view — cross-surface coherence.
 *
 * Shows the user how their agent lives across browser, desktop, and extension.
 * Each surface is part of the 6-level workflow, not directly but in parallel.
 *
 * The browser is the full workspace.
 * The desktop is the ambient companion.
 * The extension is the agent in the wild.
 *
 * All three share the same session, the same agent (Pi), the same register.
 */

import { useState, useEffect } from "react";
import { applyMembrane } from "../lib/api-clients";
import {
  SURFACE_DEFINITIONS,
  WORKFLOW_LEVELS,
  detectSurface,
  shareToDesktop,
  shareToExtension,
  type SurfaceType,
} from "../lib/surface-context";
import { INTEGRATIONS, INTEGRATION_ORDER, integrationsForSurface } from "../lib/integration-ontology";

interface SurfacesViewProps {
  isOperator: boolean;
}

export function SurfacesView({ isOperator }: SurfacesViewProps) {
  const [currentSurface] = useState<SurfaceType>(detectSurface());
  const [extensionConnected, setExtensionConnected] = useState(false);
  const [desktopConnected, setDesktopConnected] = useState(false);

  // Check if the extension is listening
  useEffect(() => {
    function onMessage(e: MessageEvent) {
      if (e.data?.type === "rida:extension:ack") {
        setExtensionConnected(true);
      }
    }
    window.addEventListener("message", onMessage);
    // Probe for extension
    window.postMessage({ type: "rida:probe" }, "*");
    return () => window.removeEventListener("message", onMessage);
  }, []);

  const membraneBypass = isOperator;

  const surfaceCards: { type: SurfaceType; connected: boolean; action: () => void; actionLabel: string }[] = [
    {
      type: "browser",
      connected: currentSurface === "browser",
      action: () => {},
      actionLabel: "This is your browser workspace",
    },
    {
      type: "desktop",
      connected: currentSurface === "desktop" || desktopConnected,
      action: () => {
        const url = shareToDesktop();
        if (url) window.location.href = url;
      },
      actionLabel: desktopConnected ? "Connected" : "Connect desktop app",
    },
    {
      type: "extension",
      connected: currentSurface === "extension" || extensionConnected,
      action: () => shareToExtension(),
      actionLabel: extensionConnected ? "Connected" : "Connect extension",
    },
  ];

  return (
    <div className="view">
      <div className="view-header">
        <span className="eyebrow">{applyMembrane("Surfaces", membraneBypass)}</span>
        <h1>Your Agent, Everywhere</h1>
        <p className="view-lede">
          Your agent lives across three surfaces — each one part of your workflow.
          Same agent. Same session. Same work history. Different posture.
        </p>
      </div>

      {/* Surface cards */}
      <div className="surfaces-grid">
        {surfaceCards.map(({ type, connected, action, actionLabel }) => {
          const def = SURFACE_DEFINITIONS[type];
          const surfaceIntegrations = integrationsForSurface(type);
          return (
            <div key={type} className={`surface-card ${connected ? "connected" : ""} ${currentSurface === type ? "current" : ""}`}>
              <div className="surface-card-header">
                <span className="surface-icon">
                  {type === "browser" ? "◐" : type === "desktop" ? "▣" : "◈"}
                </span>
                <div>
                  <div className="surface-name">{applyMembrane(def.identity, membraneBypass)}</div>
                  <div className="surface-type mono">{type}</div>
                </div>
                <span className={`surface-status ${connected ? "badge" : "badge"}`} data-status={connected ? "active" : "pending"}>
                  {connected ? "Connected" : "Available"}
                </span>
              </div>

              <p className="surface-posture">{def.posture === "full" ? "Full workspace" : def.posture === "ambient" ? "Ambient companion" : "Capture mode"}</p>

              {/* Workflow levels this surface participates in */}
              <div className="surface-levels">
                {WORKFLOW_LEVELS.filter(l => def.levels.includes(l.level)).map(level => (
                  <span key={level.level} className="surface-level-chip">
                    <span className="mono">{level.level}</span> {applyMembrane(level.name, membraneBypass)}
                  </span>
                ))}
              </div>

              {/* Integrations visible on this surface */}
              <div className="surface-integrations">
                <span className="surface-integrations-label muted">Integrations:</span>
                {surfaceIntegrations.map(src => {
                  const meta = INTEGRATIONS[src];
                  return (
                    <span key={src} className="surface-integration-chip" title={meta.label}>
                      {meta.icon} {applyMembrane(meta.label, membraneBypass)}
                    </span>
                  );
                })}
              </div>

              {/* Connect button */}
              {type !== currentSurface && (
                <button
                  className="btn-secondary surface-connect-btn"
                  onClick={action}
                  disabled={connected}
                >
                  {actionLabel}
                </button>
              )}
              {type === currentSurface && (
                <div className="surface-current-badge">You are here</div>
              )}
            </div>
          );
        })}
      </div>

      {/* The 6-level workflow */}
      <div className="section-title" style={{ marginTop: 32 }}>
        <h3>The Workflow</h3>
        <p className="muted">Each level has its own surfaces. Your agent works across all of them.</p>
      </div>

      <div className="workflow-levels">
        {WORKFLOW_LEVELS.map(level => (
          <div key={level.level} className="workflow-level-row">
            <div className="workflow-level-number">{level.level}</div>
            <div className="workflow-level-body">
              <div className="workflow-level-name">{applyMembrane(level.name, membraneBypass)}</div>
              <div className="workflow-level-desc">{applyMembrane(level.description, membraneBypass)}</div>
              <div className="workflow-level-surfaces">
                {level.surfaces.map(s => (
                  <span key={s} className="workflow-surface-chip">
                    {s === "browser" ? "◐" : s === "desktop" ? "▣" : "◈"} {s}
                  </span>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* How it works */}
      <div className="card" style={{ marginTop: 20 }}>
        <div className="card-title">How your agent works across surfaces</div>
        <div className="card-list">
          <div className="card-list-item">
            <span className="mono">1.</span> Your session is shared. Sign in once — your agent recognizes you everywhere.
          </div>
          <div className="card-list-item">
            <span className="mono">2.</span> Your agent (Pi) routes through its capability registry. On the browser: full workspace. On the desktop: ambient chat. On the extension: quick capture.
          </div>
          <div className="card-list-item">
            <span className="mono">3.</span> Every action — on any surface — receipts to the same register. Your work history is unified.
          </div>
          <div className="card-list-item">
            <span className="mono">4.</span> Integrations are bespoke at every level. Each has its own lifecycle, its own health, its own role in your workflow.
          </div>
        </div>
      </div>
    </div>
  );
}
